/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import { SocialShare } from "@quintype/components";
import PropTypes from "prop-types";
import React from "react";
import { SocialShareTemplate } from "../../../Molecules/SocialShareTemplate";
import { SectionTag } from "../../../Atoms/SectionTag";
import { StoryHeadline } from "../../../Atoms/StoryHeadline";
import { Subheadline } from "../../../Atoms/Subheadline";
import { Video } from "../../../Atoms/StoryElements/Video";
import AsideCollection from "../../AsideCollection";
import { AuthorCard } from "../../../Atoms/AuthorCard";
import { PublishDetails } from "../../../Atoms/PublishDetail";
import { StoryTags } from "../../../Atoms/StoryTags";
import { StoryElementCard } from "../../../Molecules/StoryElementCard";
import { StateProvider } from "../../../SharedContext";
import "./video-story.m.css";

const VideoStoryTemplate = ({ story = {}, config = {}, children, storyElementsConfig, widgetComp, adComponent }) => {
  const heroVideo =
    story.cards
      .flatMap(card => card["story-elements"])
      .find(
        ({ type, subtype }) => type === "youtube-video" || (type === "jsembed" && subtype === "dailymotion-video")
      ) || {};

  const {
    theme = "",
    authorCard = {},
    asideCollection = {},
    templateType = "",
    noOfVisibleCards = 0,
    publishedDetails = {},
    verticalShare = "",
    shareIconType = "plain-color-svg"
  } = config;

  const visibledCards = noOfVisibleCards < 0 ? story.cards : story.cards.slice(0, noOfVisibleCards);

  const HeroVideo = () => {
    return (
      <div styleName="hero-video index-2">
        <Video element={heroVideo} />
      </div>
    );
  };

  const VerticalShare = () => {
    return (
      <SocialShare
        template={SocialShareTemplate}
        fullUrl={story.url}
        title={story.headline}
        vertical={true}
        theme={theme}
        iconType={shareIconType}
      />
    );
  };

  const HeaderCard = () => {
    return (
      <div styleName="header-details">
        <SectionTag story={story} />
        <StoryHeadline story={story} />
        <Subheadline story={story} showFullContent />
      </div>
    );
  };

  const StoryData = () => {
    return (
      <div styleName="story-details">
        <div styleName="author">{authorCard && <AuthorCard story={story} template="default" />}</div>
        <div styleName="timestamp-social-share">
          <PublishDetails story={story} opts={publishedDetails} />
          {!verticalShare && (
            <SocialShare
              template={SocialShareTemplate}
              fullUrl={story.url}
              title={story.headline}
              theme={theme}
              iconType={shareIconType}
            />
          )}
        </div>
        {visibledCards.map(card => {
          return (
            <StoryElementCard
              heroVideoElementId={heroVideo.id}
              story={story}
              card={card}
              key={card.id}
              config={storyElementsConfig}
              adComponent={adComponent}
              widgetComp={widgetComp}
            />
          );
        })}
        <div styleName="story-tags">
          <StoryTags tags={story.tags} />
        </div>
        {children}
      </div>
    );
  };

  const SideColumn = () => {
    return (
      asideCollection && (
        <div styleName="video-story-side-column">
          <AsideCollection {...asideCollection} widgetComp={widgetComp} adComponent={adComponent} />
        </div>
      )
    );
  };

  const heroPriorityTemplate = () => {
    return (
      <>
        <HeroVideo />
        <div styleName="story-content-wrapper">
          <HeaderCard />
          <StoryData />
        </div>
        {verticalShare && <VerticalShare />}
        {asideCollection && (
          <div styleName="aside-collection tablet-index-2">
            <AsideCollection horizontal={true} {...asideCollection} widgetComp={widgetComp} adComponent={adComponent} />
          </div>
        )}
      </>
    );
  };

  const headlinePriorityTemplate = () => {
    return (
      <>
        <div styleName="story-content-wrapper">
          <HeaderCard />
        </div>
        <div styleName="story-content-wrapper index-2">
          <HeroVideo />
        </div>
        <div styleName="story-content-wrapper">
          <StoryData />
        </div>
        {verticalShare && <VerticalShare />}
        <SideColumn />
      </>
    );
  };

  const defaultTemplate = () => {
    return (
      <>
        <HeroVideo />
        <div styleName="story-content-wrapper">
          <HeaderCard />
          <StoryData />
        </div>

        {verticalShare && <VerticalShare />}
        <SideColumn />
      </>
    );
  };

  const getStoryTemplate = templateType => {
    switch (templateType) {
      case "hero-priority":
        return heroPriorityTemplate();
      case "headline-priority":
        return headlinePriorityTemplate();
      default:
        return defaultTemplate();
    }
  };

  const templateClass = templateType && templateType !== "default" ? templateType : "default";
  const dataTestId = templateType ? `video-story-${templateType}` : "video-story";
  return (
    <div
      data-test-id={dataTestId}
      className="arrow-component arr--content-wrapper arr-story-grid arr--video-story-template-wrapper"
      styleName={`${templateClass} ${verticalShare}`}
      style={{ backgroundColor: theme }}
    >
      {getStoryTemplate(templateType)}
    </div>
  );
};

VideoStoryTemplate.propTypes = {
  story: PropTypes.object,
  config: PropTypes.shape({
    templateType: PropTypes.string,
    authorCard: PropTypes.object,
    asideCollection: PropTypes.object
  }),
  children: PropTypes.node,
  storyElementsConfig: PropTypes.object,
  adComponent: PropTypes.func,
  widgetComp: PropTypes.func
};

export default StateProvider(VideoStoryTemplate);
